<script setup lang="ts">
import type { ResultsPageProperties } from '@optionway/models';
import { breakpointsTailwind } from '@vueuse/core';

const { $appConfig, $resetOfferExpiration } = useNuxtApp();
const config = $appConfig.app?.pages?.results;

const pageConfig = computed<ResultsPageProperties>(() => {
  return {
    components: config?.components,
    hideComponents: config?.hideComponents,
  };
});

const { queryLoading, error } = useSearchResultsQueryState();

const criteriaStore = useSearchCriteriaStore();
const locationLabels = useLocationLabels();
const { t } = useI18n();
const fullLocale = useFullLocale();
const [searchCriteriaPanelOpen, toggleSearchCriteriaPanel] = useToggle(false);

// Empty at setup, so the lock is inert on the server; the mount hands it the document body.
const scrollLockTarget = ref<HTMLElement>();
const isScrollLocked = useScrollLock(scrollLockTarget);
syncRef(searchCriteriaPanelOpen, isScrollLocked, { direction: 'ltr' });

// The search criteria panel must end at the viewport bottom, but its top depends on the header, which scrolls
// away and can be hidden by the partner config. The measured summary bottom (stable while the scroll
// lock holds) feeds a CSS custom property: a runtime-measured value has no mechanism other than :style.
const summaryElement = ref<HTMLElement>();
const { bottom: summaryBottom } = useElementBounding(summaryElement);

// Below lg the search criteria panel fills the screen down to the corner the Beacon fab sits in.
const isMobile = useBreakpoints(breakpointsTailwind).smaller('lg');
useBeaconFabHidden(() => searchCriteriaPanelOpen.value && isMobile.value);

// A completed search that didn't error is a fresh GDS quote: (re-)arm the timer from that moment.
// 0 results is fine — it can come from over-filtering while a valid underlying quote still exists.
watch(queryLoading, (loading, wasLoading) => {
  if (wasLoading && !loading && !error.value) {
    $resetOfferExpiration();
  }
});

// t() and location labels cannot be called in a store --> exception to the no-store-reads-in-computed rule.
// No manual brand suffix: the titleTemplate (site.name) already appends it — the old literal
// produced « Paris → Rome – Option Way | Option Way » on the most indexed page of the site.
const ogTitle = computed(() =>
  criteriaStore.hasRequiredCriteria
    ? `${locationLabels.city(criteriaStore.committedOriginCode)} → ${locationLabels.city(criteriaStore.committedDestinationCode)}`
    : undefined,
);

const ogDates = computed(() => {
  if (!criteriaStore.departureDate) return '';
  const dep = formatDateShort(criteriaStore.departureDate, fullLocale.value);
  return criteriaStore.returnDate ? `${dep} – ${formatDateShort(criteriaStore.returnDate, fullLocale.value)}` : dep;
});

const ogDescription = computed(() => {
  if (!criteriaStore.hasRequiredCriteria) return;
  const pax = t('components.search.passengersTypes.paxSummary', criteriaStore.committedPaxCount);
  const cabin = t(`components.search.cabinClassSelector.${criteriaStore.searchRequest.cabinClass}`);
  return `${ogDates.value} · ${pax} · ${cabin}`;
});

// description stays with the localized key (app.vue, pages.results.seo.description): the raw
// « dates · pax · cabin » summary is share material (og:description), not an indexable sentence.
useSeoMeta({ title: ogTitle, ogTitle, ogDescription });

definePageMeta({
  title: 'pages.results.seo.title',
  description: 'pages.results.seo.description',
  middleware: ['entry-point'],
});

const onSearchCriteriaPanelToggle = () => {
  if (!queryLoading.value) {
    // Opening: discard uncommitted edits left over from a previous cancel-free close.
    // Closing via this same button ("Annuler"): discard the edits just made, they were never searched.
    criteriaStore.revertToCommittedCriteria();
    toggleSearchCriteriaPanel();
  }
};

const onCancelSearchCriteriaPanel = () => {
  criteriaStore.revertToCommittedCriteria();
  toggleSearchCriteriaPanel(false);
};
onMounted(() => {
  scrollLockTarget.value = document.body;
});
</script>

<template>
  <div class="flex w-full flex-col items-center">
    <UiCustomFadeTransition>
      <div
        v-if="criteriaStore.hasSearched || searchCriteriaPanelOpen"
        ref="summaryElement"
        class="sticky top-0 z-20 w-full"
      >
        <CommonFlightSummary
          :editing="searchCriteriaPanelOpen"
          @edit="onSearchCriteriaPanelToggle"
        />

        <UiCustomFadeTransition>
          <div
            v-if="searchCriteriaPanelOpen"
            class="absolute left-0 top-full max-h-screen-below-flight-summary w-full overflow-y-auto overscroll-contain bg-background"
            :style="{ '--flight-summary-bottom': `${summaryBottom}px` }"
          >
            <div class="w-full px-4 py-6 md:pb-6 md:pt-5 lg:px-8">
              <UiCustomContainer>
                <SearchForm @search="toggleSearchCriteriaPanel(false)" />
              </UiCustomContainer>
            </div>
          </div>
        </UiCustomFadeTransition>
      </div>
    </UiCustomFadeTransition>

    <UiCustomFadeTransition>
      <button
        v-if="searchCriteriaPanelOpen"
        :aria-label="$t('components.common.flightSummary.cancelEditing')"
        class="fixed inset-0 z-10 cursor-pointer bg-black/40"
        type="button"
        @click="onCancelSearchCriteriaPanel"
      />
    </UiCustomFadeTransition>

    <UiCustomFadeTransition>
      <div
        v-if="
          (!criteriaStore.hasSearched || criteriaStore.fromDeepLink || criteriaStore.refreshRequested) &&
          !searchCriteriaPanelOpen
        "
        class="flex w-full flex-col"
      >
        <hr class="my-3" />

        <div class="w-full p-4 md:p-6 lg:p-8">
          <UiCustomContainer>
            <SearchForm />
          </UiCustomContainer>
        </div>
      </div>
    </UiCustomFadeTransition>

    <div class="flex w-full justify-center bg-gray-50 pb-10">
      <ResultsList v-if="!pageConfig.hideComponents?.resultsList" />
    </div>
  </div>
</template>
